import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from 'antd';
import { pendingUpdatesApi } from '../../services/api';

// Icon for data confirmation
const DataIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const DataConfirmationSection: React.FC = () => {
  const navigate = useNavigate();

  // Fetch pending updates count
  // Note: api interceptor already unwraps response.data, so we get the data directly
  const { data: countData } = useQuery({
    queryKey: ['pending-updates-count'],
    queryFn: async () => {
      // The interceptor returns data directly, but TypeScript thinks it's AxiosResponse
      const data = await pendingUpdatesApi.getCount() as unknown as { count: number };
      return data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const count = countData?.count || 0;

  // Don't render if no pending updates
  if (count === 0) {
    return null;
  }

  const handleClick = () => {
    navigate('/pending-updates');
  };

  return (
    <div
      className="data-confirmation-banner"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      aria-label={`${count} data confirmation${count !== 1 ? 's' : ''} pending review`}
    >
      <div className="data-confirmation-content">
        <div className="data-confirmation-icon">
          <DataIcon />
        </div>
        <div className="data-confirmation-text">
          <span className="data-confirmation-title">
            Data Confirmation Required
          </span>
          <span className="data-confirmation-description">
            {count} update{count !== 1 ? 's' : ''} extracted from documents need{count === 1 ? 's' : ''} your review
          </span>
        </div>
        <Badge
          count={count}
          style={{
            backgroundColor: '#fa8c16',
            fontWeight: 600,
          }}
        />
        <div className="data-confirmation-action">
          <CheckIcon />
          <span>Review Now</span>
        </div>
      </div>
    </div>
  );
};

export default DataConfirmationSection;
